/**
 * Business Process Edit Route
 *
 * Edit an existing business process using BusinessProcessForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useBusinessProcess } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { BusinessProcessForm } from '../../components/business-processes';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import {
  updateBusinessProcess,
  type UpdateBusinessProcessInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreateBusinessProcessFormData } from '../../components/forms';
import './edit.css';

export const Route = createFileRoute('/business-processes/$businessProcessId/edit')({
  component: BusinessProcessEditPage,
});

function BusinessProcessEditPage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const { businessProcessId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch business process data
  const { data, loading, error: fetchError } = useBusinessProcess(businessProcessId);
  const businessProcess = data?.businessProcess;

  if (loading) {
    return (
      <div className="business-process-edit-page">
        <LoadingSpinner centered message="Loading business process..." />
      </div>
    );
  }

  if (fetchError || !businessProcess) {
    return (
      <div className="business-process-edit-page">
        <EmptyState
          icon="error"
          title="Failed to load business process"
          description={fetchError?.message || 'Business process not found'}
          actionLabel="Back to Business Processes"
          onAction={() =>
            navigate({ to: '/business-processes', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  const handleSubmit = async (formData: CreateBusinessProcessFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build update input
      const updates: UpdateBusinessProcessInput = {
        name: formData.name,
        description: formData.description || undefined,
        processType: formData.processType,
        processTier: formData.processTier,
        processOwnerId: formData.processOwnerId,
        owningBusinessUnitId: formData.owningBusinessUnitId,
        inputDescription: formData.inputDescription || undefined,
        outputDescription: formData.outputDescription || undefined,
        estimatedDuration: formData.estimatedDuration || undefined,
        volumePerPeriod: formData.volumePerPeriod || undefined,
        automationLevel: formData.automationLevel || undefined,
        toolingDescription: formData.toolingDescription || undefined,
        status: formData.status || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      await updateBusinessProcess(businessProcessId, updates);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['GetBusinessProcess', 'ListBusinessProcesses'],
      });

      // Navigate back to business process detail page
      navigate({ to: `/business-processes/${businessProcessId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: `/business-processes/${businessProcessId}` as string });
  };

  // Map business process data to form default values
  const defaultValues: Partial<CreateBusinessProcessFormData> = {
    name: businessProcess.name,
    description: businessProcess.description || undefined,
    processType: businessProcess.processType,
    processTier: businessProcess.processTier,
    processOwnerId: businessProcess.processOwnerId || undefined,
    owningBusinessUnitId: businessProcess.owningBusinessUnitId || undefined,
    inputDescription: businessProcess.inputDescription || undefined,
    outputDescription: businessProcess.outputDescription || undefined,
    estimatedDuration: businessProcess.estimatedDuration || undefined,
    volumePerPeriod: businessProcess.volumePerPeriod || undefined,
    automationLevel: (businessProcess.automationLevel || undefined) as
      'manual' | 'semi-automated' | 'automated' | 'autonomous' | undefined,
    toolingDescription: businessProcess.toolingDescription || undefined,
    status: businessProcess.status || undefined,
    tags: businessProcess.tags || [],
    notes: businessProcess.notes || undefined,
  };

  return (
    <div className="business-process-edit-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Business Process</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update business process information
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <BusinessProcessForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
